/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    string, shape, bool, object,
} from 'prop-types';
import mbpLogger from '@module-logger/mbp';
import { useDispatch, useSelector } from 'react-redux';

import ComponentErrorBoundary from '../EnterpriseID/Common/ComponentErrorBoundary/ComponentErrorBoundary';
import CarouselBuilderSkeleton from '../GraphqlSkeletonComponents/CarouselBuilderSkeleton';
import PersonalizedCarouselBuilder from '../EnterpriseID/Common/PersonalizedCarouselBuilder';
import useClickStreamCustomEventsTracking from '../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import useIsPlaPage from '../../../helpers/hooks/useIsPlaPage';
import { getBrand } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getBrandName } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import useExperience from '../../../helpers/Personalization/useExperience';

const ProductCarousel = ({
    experienceData,
    recommendationData,
    isPdpRedesignEnabled,
    isPdpRedesignShortEnabled,
    isPlaRedesignEnabled,
    productName,
    dataTestId,
}) => {
    const {
        products_to_show = undefined,
        title_font_color,
        title_font_size,
        title_font_family,
        title_font_style,
        title_font_weight,
    } = recommendationData || {};

    // Extract the title from campaign response
    const title = experienceData?.experiment?.value?.productRecRecipe?.title;
    const maxProducts = experienceData?.experiment?.value?.productRecRecipe?.maxProducts || 8;
    const brand = useSelector(getBrand);
    const dispatch = useDispatch();

    const trackClickThrough = useClickStreamCustomEventsTracking({
        experimentTracking: experienceData?.experimentTracking,
        isFireImpression: true,
        page: { type: 'product' },
    });

    const trackClickEvent = (product) => {
        trackClickThrough(product);
        dispatch(trackEvent({
            eventName: 'feature_interaction',
            eventCategory: 'Product Page',
            eventAction: 'Recommended for You Clicks',
            feature_detail: `${product} - ${brand.id}`,
            feature_element: 'Recommended for You Clicks',
            feature_category: 'Product Page',
        }));
    };

    return (
        <ComponentErrorBoundary function="ProductRecommendationsMulitSF" module="PersonalizedContent">
            <PersonalizedCarouselBuilder
                blockData={{
                    products_to_show,
                    title: title || 'Recommended For You',
                    title_font_color: title_font_color?.color,
                    title_font_size,
                    title_font_style,
                    title_font_family,
                    title_font_weight,
                    wcs_style: false,
                }}
                productClickCallback={trackClickEvent}
                products={experienceData?.products || []}
                nDisplayProducts={maxProducts}
                demandType="XS"
                attributionToken={experienceData?.attributionToken || ''}
                isPdpRedesignEnabled={isPdpRedesignEnabled}
                isPdpRedesignShortEnabled={isPdpRedesignShortEnabled}
                isPlaRedesignEnabled={isPlaRedesignEnabled}
                carouselUIComponent="productRecommendationsCarousel"
                productName={productName}
                dataTestId={dataTestId}
            />
        </ComponentErrorBoundary>
    );
};

ProductCarousel.propTypes = {
    experienceData: object.isRequired,
    recommendationData: object.isRequired,
    isPdpRedesignEnabled: bool,
    isPdpRedesignShortEnabled: bool,
    isPlaRedesignEnabled: bool,
    productName: string,
    dataTestId: string,
};

ProductCarousel.defaultProps = {
    isPdpRedesignEnabled: false,
    isPdpRedesignShortEnabled: false,
    isPlaRedesignEnabled: false,
    productName: '',
    dataTestId: '',
};

const ProductRecommendationsSFContainer = ({
    recommendationData, isSympathySku, partNumber, productName, isPdpRedesignEnabled, isPdpRedesignShortEnabled, isPlaRedesignEnabled, dataTestId,
}) => {
    const {
        products_to_show = undefined,
        campaign_id: campaignId = '',
    } = recommendationData || {};

    const brand = useSelector(getBrandName) || '1800flowers';

    const isPlaPage = useIsPlaPage();

    const campaignFeature = isPlaPage ? `${brand} - Get Campaign - PLA Recs` : campaignId;

    const {
        loading, error, data, variables,
    } = useExperience({
        queryName: 'PDPSimilarProductRecs',
        type: 'product',
        interactionName: campaignFeature,
        userAttributes: [
            { name: 'isSympathy', value: isSympathySku ? 'true' : 'false' },
            { name: 'referenceProductId', value: partNumber },
            { name: 'productName', value: productName },
        ],
        targeting: {
            pageType: 'product',
        },
        filterByZipCode: true,
        resolveIdentity: true,
    });

    if (loading || variables?.skip) {
        return (
            <CarouselBuilderSkeleton
                nDisplayProducts={products_to_show}
                breakPoints={{ mobile: 601, desktop: 769 }}
            />
        );
    }

    if (error) {
        mbpLogger.logError({
            function: 'ProductRecommendationsSFContainer.js',
            message: 'Error loading data from saleforce api',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: error,
        });
    }

    if (data?.experience?.products?.length > 0) {
        return (
            <ProductCarousel
                experienceData={data?.experience}
                recommendationData={recommendationData}
                isPdpRedesignEnabled={isPdpRedesignEnabled}
                isPdpRedesignShortEnabled={isPdpRedesignShortEnabled}
                isPlaRedesignEnabled={isPlaRedesignEnabled}
                productName={productName}
                dataTestId={dataTestId}
            />
        );
    }

    return null;
};

ProductRecommendationsSFContainer.propTypes = {
    isSympathySku: bool.isRequired,
    partNumber: string,
    productName: string,
    recommendationData: shape({
        title_font_color: shape({
            color: string.isRequired,
        }),
        title_font_size: string,
        title_font_style: string,
        title_font_family: string,
    }).isRequired,
    isPdpRedesignEnabled: bool,
    isPdpRedesignShortEnabled: bool,
    isPlaRedesignEnabled: bool,
    dataTestId: string,
};

ProductRecommendationsSFContainer.defaultProps = {
    partNumber: '',
    productName: '',
    isPdpRedesignEnabled: false,
    isPdpRedesignShortEnabled: false,
    isPlaRedesignEnabled: false,
    dataTestId: '',
};

export default ProductRecommendationsSFContainer;
